<template>
  <DxPopup
    :visible="visible"
    :title="dialogTitle"
    :show-close-button="true"
    :width="popupWidth"
    :max-width="850"
    :height="'auto'"
    :max-height="'85vh'"
    @hidden="onHidden"
  >
    <template #content>
      <div class="feedback-dialog-content">
        <!-- 1. SUCCESS MODE -->
        <div v-if="mode === 'SUCCESS'" class="feedback-mode feedback-mode--success">
          <div class="status-banner status-banner--success">
            <div class="status-icon status-icon--success">
              <i class="fa fa-check-circle"></i>
            </div>
            <div class="status-info">
              <h5 class="status-title">{{ $t(workOnlySelection ? "tools.erp_integration.consum_work_invoice_success_heading" : "tools.erp_integration.consum_feedback_full_success_heading") }}</h5>
              <p v-if="!workOnlySelection" class="status-subtitle">
                {{ $t("tools.erp_integration.consum_feedback_success_desc", { count: documentSummary.count || 1 }) }}
              </p>
            </div>
          </div>

          <div v-if="!workOnlySelection" class="details-card">
            <div class="stats-grid">
              <div class="stat-box">
                <span class="stat-label">{{ $t("tools.erp_integration.consum_feedback_doc_number") }}</span>
                <span class="stat-value text-success">
                  {{ documentSummary.numar || '-' }}
                </span>
              </div>
              <div class="stat-box">
                <span class="stat-label">{{ $t("tools.erp_integration.consum_feedback_doc_series") }}</span>
                <span class="stat-value">
                  {{ documentSummary.serie || '-' }}
                </span>
              </div>
              <div class="stat-box">
                <span class="stat-label">{{ $t("tools.erp_integration.consum_feedback_doc_code") }}</span>
                <span class="stat-value">
                  {{ documentSummary.cod_ies || '-' }}
                </span>
              </div>
              <div class="stat-box">
                <span class="stat-label">{{ $t("tools.erp_integration.consum_feedback_doc_count") }}</span>
                <span class="stat-value">
                  {{ documentSummary.count || 1 }}
                </span>
              </div>
            </div>
          </div>

          <div class="feedback-hint">
            <i class="fa fa-info-circle me-1"></i>
            {{ $t("tools.erp_integration.consum_feedback_success_hint") }}
          </div>
        </div>

        <div v-else-if="mode === 'PARTIAL_SUCCESS'" class="feedback-mode feedback-mode--warning">
          <div class="status-banner status-banner--warning">
            <div class="status-icon status-icon--warning">
              <i class="fa fa-exclamation-triangle"></i>
            </div>
            <div class="status-info">
              <h5 class="status-title">{{ $t("tools.erp_integration.consum_feedback_partial_heading") }}</h5>
              <p class="status-subtitle">{{ $t(workOnlySelection ? "tools.erp_integration.consum_work_invoice_partial_desc" : "tools.erp_integration.consum_feedback_partial_desc") }}</p>
            </div>
          </div>
        </div>

        <!-- ALREADY ATTEMPTED MODE -->
        <div v-else-if="mode === 'ALREADY_ATTEMPTED'" class="feedback-mode feedback-mode--warning">
          <div class="status-banner status-banner--warning">
            <div class="status-icon status-icon--warning">
              <i class="fa fa-ban"></i>
            </div>
            <div class="status-info">
              <h5 class="status-title">{{ $t("tools.erp_integration.consum_feedback_already_attempted_title") }}</h5>
              <p class="status-subtitle">
                {{ message || $t("tools.erp_integration.consum_feedback_already_attempted_hint") }}
              </p>
            </div>
          </div>

          <div v-if="queueUuid" class="details-card my-3 p-3">
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <span class="text-muted small d-block">QUEUE UUID</span>
                <code>{{ queueUuid }}</code>
                <div class="text-muted small mt-1">
                  {{ $t("tools.erp_integration.consum_feedback_already_attempted_hint") }}
                </div>
              </div>
              <button
                v-if="!unblockSuccess"
                type="button"
                class="btn btn-danger btn-sm"
                :disabled="cancelingQueue"
                @click="onCancelBlockingQueue"
              >
                <i v-if="cancelingQueue" class="fa fa-spinner fa-spin me-1"></i>
                <i v-else class="fa fa-times-circle me-1"></i>
                {{ $t("tools.erp_integration.consum_cancel_and_unblock") }}
              </button>
            </div>
          </div>

          <div v-if="unblockSuccess" class="alert alert-success mt-2 mb-0">
            <i class="fa fa-check-circle me-1"></i>
            {{ $t("tools.erp_integration.consum_ready_to_resend") }}
          </div>
        </div>

        <!-- 2. ERP ERROR MODE -->
        <div v-else-if="mode === 'ERP_ERROR'" class="feedback-mode feedback-mode--error">
          <div class="status-banner status-banner--danger">
            <div class="status-icon status-icon--danger">
              <i class="fa fa-exclamation-triangle"></i>
            </div>
            <div class="status-info">
              <h5 class="status-title">{{ $t("tools.erp_integration.consum_feedback_error_heading") }}</h5>
              <p class="status-subtitle">
                {{ $t("tools.erp_integration.consum_feedback_error_desc") }}
              </p>
            </div>
          </div>

          <div class="error-list-container">
            <div class="error-list-title">
              <i class="fa fa-bug me-1"></i>
              {{ $t("tools.erp_integration.consum_feedback_error_messages_title") }}:
            </div>
            <ul class="error-list">
              <li v-for="(err, idx) in errorList" :key="'err-' + idx" class="error-item">
                <code>{{ err }}</code>
              </li>
            </ul>
          </div>

          <div class="feedback-hint feedback-hint--danger">
            <i class="fa fa-exclamation-circle me-1"></i>
            {{ $t("tools.erp_integration.consum_feedback_error_hint") }}
          </div>
        </div>

        <!-- 3. MISSING MAPPINGS MODE -->
        <div v-else-if="mode === 'MISSING_MAPPINGS'" class="feedback-mode feedback-mode--warning">
          <div class="status-banner status-banner--warning">
            <div class="status-icon status-icon--warning">
              <i class="fa fa-exclamation-circle"></i>
            </div>
            <div class="status-info">
              <h5 class="status-title">{{ $t("tools.erp_integration.consum_feedback_missing_heading") }}</h5>
              <p class="status-subtitle">
                {{ $t("tools.erp_integration.consum_feedback_missing_desc", { count: issuesList.length }) }}
              </p>
            </div>
          </div>

          <div class="issues-table-wrap">
            <table class="issues-table">
              <thead>
                <tr>
                  <th style="width: 18%">{{ $t("tools.erp_integration.consum_feedback_missing_col_type") }}</th>
                  <th style="width: 25%">{{ $t("tools.erp_integration.consum_feedback_missing_col_name") }}</th>
                  <th style="width: 15%">{{ $t("tools.erp_integration.consum_feedback_missing_col_field") }}</th>
                  <th style="width: 27%">{{ $t("tools.erp_integration.consum_feedback_missing_col_reason") }}</th>
                  <th style="width: 15%; text-align: center;">{{ $t("tools.erp_integration.consum_feedback_missing_col_action") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(issue, idx) in issuesList" :key="'issue-' + idx">
                  <td>
                    <span class="type-badge" :class="'type-badge--' + (issue.type || 'default').toLowerCase()">
                      {{ getEntityTypeName(issue.type) }}
                    </span>
                  </td>
                  <td>
                    <strong>{{ displayIssueName(issue) }}</strong>
                    <div v-if="displayIssueKey(issue)" class="item-sub-key">
                      <code>{{ displayIssueKey(issue) }}</code>
                    </div>
                  </td>
                  <td>
                    <code class="field-badge">{{ issue.field || '-' }}</code>
                  </td>
                  <td>
                    <span class="text-danger">{{ getIssueReason(issue) }}</span>
                  </td>
                  <td style="text-align: center;">
                    <button
                      v-if="hasIssueAction(issue)"
                      type="button"
                      class="btn btn-xs btn-outline-primary configure-btn"
                      :title="getIssueActionLabel(issue)"
                      @click="goToIssueTarget(issue)"
                    >
                      <i :class="isPriceIssue(issue) ? 'fa fa-coins me-1' : 'fa fa-wrench me-1'"></i>
                      {{ getIssueActionLabel(issue) }}
                    </button>
                    <span v-else class="text-muted">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="feedback-hint feedback-hint--warning">
            <i class="fa fa-info-circle me-1"></i>
            {{ $t("tools.erp_integration.consum_feedback_missing_hint") }}
          </div>
        </div>

        <div v-if="invoiceExports.length" class="details-card">
          <h6 class="mb-3 font-weight-bold">
            <i class="fa fa-file-invoice me-1"></i>
            {{ $t("tools.erp_integration.consum_feedback_invoice_results") }}
          </h6>
          <div
            v-for="(item, index) in invoiceExports"
            :key="item.uuid || index"
            class="mb-3 p-3 border rounded bg-white"
          >
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <strong>#{{ index + 1 }} {{ invoiceItemName(item) }}</strong>
                <span v-if="item.wm_cod_ies" class="ms-2 text-muted">CodIes: {{ item.wm_cod_ies }}</span>
              </div>
              <span class="badge" :class="item.status === 'CONFIRMED' ? 'badge-success' : (item.status === 'BLOCKED' ? 'badge-danger' : 'badge-warning')">
                {{ invoiceStatusLabel(item.status) }}
              </span>
            </div>
            <div v-if="outgoingInvoiceDetails(item)" class="small text-muted mt-2">
              <span>CodSubunitate: {{ outgoingInvoiceDetails(item).seller || "-" }}</span>
              <span class="ms-2">CodClient: {{ outgoingInvoiceDetails(item).buyer || "-" }}</span>
              <span class="ms-2">NrDoc: {{ outgoingInvoiceDetails(item).number || "-" }}</span>
            </div>

            <div v-if="item.error" class="alert alert-danger p-2 mt-2 mb-2 small">
              <strong>{{ $t("tools.erp_integration.consum_feedback_error_label") }}:</strong> {{ item.error }}
            </div>

            <!-- Missing mappings / Issues table for this invoice -->
            <div v-if="(item.issues && item.issues.length) || (item.missing_mappings && item.missing_mappings.length)" class="mt-2">
              <div class="font-weight-bold small text-danger mb-1">
                <i class="fa fa-exclamation-triangle me-1"></i>
                {{ $t("tools.erp_integration.consum_feedback_missing_mappings", { count: (item.issues || item.missing_mappings).length }) }}
              </div>
              <div class="table-responsive">
                <table class="issues-table mb-0">
                  <thead>
                    <tr>
                      <th style="width: 20%">{{ $t("tools.erp_integration.consum_feedback_missing_col_type") }}</th>
                      <th style="width: 30%">{{ $t("tools.erp_integration.consum_feedback_missing_col_name") }}</th>
                      <th style="width: 15%">{{ $t("tools.erp_integration.consum_feedback_missing_col_field") }}</th>
                      <th style="width: 35%">{{ $t("tools.erp_integration.consum_feedback_missing_col_reason") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(iss, iIdx) in (item.issues || item.missing_mappings)" :key="iIdx">
                      <td>
                        <span class="type-badge" :class="'type-badge--' + (iss.type || 'default').toLowerCase()">
                          {{ getEntityTypeName(iss.type) }}
                        </span>
                      </td>
                      <td>
                        <strong>{{ displayIssueName(iss) }}</strong>
                      </td>
                      <td><code>{{ iss.field }}</code></td>
                      <td class="text-danger">{{ getIssueReason(iss) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- ACTIONS FOOTER -->
        <div class="feedback-actions">
          <button
            v-if="mode === 'MISSING_MAPPINGS'"
            type="button"
            class="btn btn-outline-primary btn-sm me-auto"
            @click="goToNomenclatures"
          >
            <i class="fa fa-external-link-alt me-1"></i>
            {{ $t("tools.erp_integration.consum_feedback_open_nomenclatures") }}
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="mode === 'SUCCESS' ? 'btn-success' : 'btn-secondary'"
            @click="close"
          >
            {{ mode === 'SUCCESS' ? $t("common.ok") : $t("common.close") }}
          </button>
        </div>
      </div>
    </template>
  </DxPopup>
</template>

<script>
import DxPopup from "devextreme-vue/popup";
import notify from "devextreme/ui/notify";
import { issueDisplayName, issueDisplayKey, issueDisplayReason } from "../issueDisplay";

export default {
  name: "ConsumExportFeedbackDialog",
  components: {
    DxPopup,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "SUCCESS", // 'SUCCESS' | 'ERP_ERROR' | 'MISSING_MAPPINGS' | 'ALREADY_ATTEMPTED'
    },
    document: {
      type: Object,
      default: () => ({}),
    },
    errors: {
      type: [Array, Object, String],
      default: () => [],
    },
    issues: {
      type: Array,
      default: () => [],
    },
    invoiceExports: {
      type: Array,
      default: () => [],
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    message: {
      type: String,
      default: "",
    },
    queueUuid: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      cancelingQueue: false,
      unblockSuccess: false,
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.cancelingQueue = false;
        this.unblockSuccess = false;
      }
    },
  },
  computed: {
    workOnlySelection() {
      return this.selectedRows.length > 0 && this.selectedRows.every((row) => row.has_consum === false);
    },
    popupWidth() {
      return "90%";
    },
    dialogTitle() {
      if (this.mode === "SUCCESS") {
        return this.$t("tools.erp_integration.consum_feedback_success_title");
      }
      if (this.mode === "ALREADY_ATTEMPTED") {
        return (
          this.$t("tools.erp_integration.consum_feedback_already_attempted_title")
        );
      }
      if (this.mode === "ERP_ERROR") {
        return this.$t("tools.erp_integration.consum_feedback_error_title");
      }
      if (this.mode === "PARTIAL_SUCCESS") {
        return this.$t("tools.erp_integration.consum_feedback_partial_heading");
      }
      return this.$t("tools.erp_integration.consum_feedback_missing_title");
    },
    documentSummary() {
      return this.document || {};
    },
    errorList() {
      const normalize = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) {
          return val.flatMap((item) => (typeof item === "object" ? normalize(item) : [String(item)]));
        }
        if (typeof val === "object") {
          const list = [];
          if (val.error) {
            list.push(...normalize(val.error));
          }
          Object.entries(val).forEach(([k, v]) => {
            if (k === "error" || k === "type") return;
            list.push(...normalize(v));
          });
          if (list.length) return list;
          return [JSON.stringify(val)];
        }
        return [String(val)];
      };

      const extracted = normalize(this.errors);
      if (extracted.length) {
        return extracted;
      }
      if (this.message) {
        return [this.message];
      }
      return [this.$t("tools.erp_integration.consum_feedback_error_generic")];
    },
    issuesList() {
      return Array.isArray(this.issues) ? this.issues : [];
    },
  },
  methods: {
    invoiceItemName(item) {
      const ids = item.consum_uuids || [item.consum_uuid];
      const workIds = item.work_sheet_uuids || [item.work_sheet_uuid];
      const rows = this.selectedRows.filter((row) => ids.includes(row.uuid)
        || (row.has_consum === false && workIds.includes(row.work_sheet_uuid)));
      const productNames = [...new Set(rows.map((row) => row.has_consum === false ? row.work_name : row.product_name).filter(Boolean))];
      const buyerName = rows[0]?.parcel_legal_entity_name || rows[0]?.parcel_department_name;
      return [productNames.join(", "), buyerName].filter(Boolean).join(" — ")
        || this.$t("tools.erp_integration.consum_feedback_unknown_entry");
    },
    displayIssueName(issue) {
      return issueDisplayName(issue, this.$t("tools.erp_integration.consum_feedback_unknown_entry"));
    },
    displayIssueKey(issue) {
      return issueDisplayKey(issue);
    },
    displayIssueReason(issue) {
      return issueDisplayReason(issue, this.$t("tools.erp_integration.consum_feedback_unknown_entry"));
    },
    outgoingInvoiceDetails(item) {
      const payload = item?.iesiri_payload;
      if (!payload) return null;
      let parsed = payload;
      if (typeof payload === "string") {
        try {
          parsed = JSON.parse(payload);
        } catch (e) {
          return null;
        }
      }
      if (!parsed || typeof parsed !== "object") return null;
      const document = Array.isArray(parsed.Documente) ? parsed.Documente[0] : null;
      return { seller: parsed.CodSubunitate, buyer: document?.CodClient, number: document?.NrDoc };
    },
    invoiceStatusLabel(status) {
      const key = `tools.erp_integration.consum_invoice_status_${String(status || "").toLowerCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    close() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    onHidden() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    getEntityTypeName(type) {
      switch (type) {
        case "PRODUCT":
          return this.$t("common.product");
        case "MEASURE_UNIT":
          return this.$t("common.measure_unit");
        case "SEASONS":
          return this.$t("common.season");
        case "WAREHOUSE":
          return this.$t("common.warehouse");
        case "SUBUNITATE":
          return this.$t("tools.erp_integration.consum_feedback_entity_subunitate");
        case "PARCEL":
          return this.$t("tools.erp_integration.consum_feedback_entity_parcel");
        case "PARCEL_WAREHOUSE":
          return this.$t("tools.erp_integration.consum_feedback_entity_parcel_warehouse");
        case "WORK_TYPES":
          return this.$t("tools.erp_integration.consum_feedback_entity_work_types");
        case "WORK":
          return this.$t("tools.erp_integration.consum_feedback_entity_work");
        case "PARCEL_SURFACE":
          return this.$t("tools.erp_integration.consum_feedback_entity_parcel_surface");
        case "CONSUM_ITEM_PRICE":
          return this.$t("tools.erp_integration.consum_feedback_entity_consum_item_price");
        case "CONSUM_ITEM_QUANTITY":
          return this.$t("tools.erp_integration.consum_feedback_entity_consum_item_quantity");
        case "UNIT_PRICE":
          return this.$t("tools.erp_integration.consum_feedback_entity_unit_price");
        case "WORK_UNIT_PRICE":
          return this.$t("tools.erp_integration.consum_feedback_entity_work_unit_price");
        case "CONSUM":
          return this.$t("tools.erp_integration.consum_feedback_entity_consum_quantity");
        default:
          return type || "-";
      }
    },
    getIssueReason(issue) {
      const typeKey = (issue.type || "").toLowerCase();
      const translationKey = `tools.erp_integration.consum_missing_reason_${typeKey}`;
      if (this.$te(translationKey)) {
        return this.$t(translationKey, {
          field: issue.field || "",
          name: issue.name || this.$t("tools.erp_integration.consum_feedback_unknown_entry"),
          date: issue.consum_date || "-",
          department: issue.department_name || issue.department_uuid || "-",
        });
      }
      return this.displayIssueReason(issue) || "-";
    },
    isPriceIssue(issue) {
      return ["UNIT_PRICE", "WORK_UNIT_PRICE"].includes(issue?.type);
    },
    hasIssueAction(issue) {
      return !["CONSUM", "QUANTITY"].includes(issue?.type);
    },
    getIssueActionLabel(issue) {
      if (issue?.type === "UNIT_PRICE") {
        return this.$t("tools.erp_integration.consum_feedback_manage_product_prices");
      }
      if (issue?.type === "WORK_UNIT_PRICE") {
        return this.$t("tools.erp_integration.consum_feedback_manage_work_prices");
      }
      return this.$t("tools.erp_integration.consum_feedback_missing_btn_configure");
    },
    goToIssueTarget(issue) {
      if (issue?.type === "UNIT_PRICE" || issue?.type === "WORK_UNIT_PRICE") {
        this.close();
        if (this.$router) {
          this.$router.push(issue.type === "UNIT_PRICE"
            ? "/stock-management/sm-products"
            : "/works/work-group-list");
        }
        return;
      }
      this.goToMapping(issue);
    },
    goToNomenclatures() {
      this.close();
      if (this.$router) {
        this.$router.push("/tools/erp-integration/nomenclatures");
      }
    },
    goToMapping(issue) {
      this.close();
      let entityCode = "PRODUCTS";
      switch (issue.type) {
        case "PRODUCT":
          entityCode = "PRODUCTS";
          break;
        case "WAREHOUSE":
          entityCode = "WAREHOUSES";
          break;
        case "MEASURE_UNIT":
          entityCode = "MEASURE_UNITS";
          break;
        case "SUBUNITATE":
          entityCode = "LEGAL_ENTITIES";
          break;
        case "PARCEL":
        case "PARCEL_WAREHOUSE":
          entityCode = "PARCELS";
          break;
        case "WORK_TYPES":
          entityCode = "WORK_TYPES";
          break;
        default:
          entityCode = issue.type || "PRODUCTS";
      }

      if (this.$router) {
        this.$router.push({
          path: "/tools/erp-integration/nomenclatures",
          query: { entity: entityCode, view: "mappings" },
        });
      }
    },
    async onCancelBlockingQueue() {
      if (!this.queueUuid) return;
      this.cancelingQueue = true;
      try {
        await this.$store.dispatch("erpIntegration/cancelConsumQueueItem", {
          uuid: this.queueUuid,
          reason: this.$t("tools.erp_integration.consum_unblocked_from_resend"),
        });
        this.unblockSuccess = true;
        notify(
          this.$t("tools.erp_integration.consum_ready_to_resend"),
          "success",
          3000
        );
        this.$emit("unblocked", this.queueUuid);
      } catch (err) {
        notify(
          err?.response?.data?.message || err?.message || this.$t("tools.erp_integration.consum_unblock_error"),
          "error",
          4000
        );
      } finally {
        this.cancelingQueue = false;
      }
    },
  },
};
</script>

<style scoped>
::v-deep .dx-popup-content {
  padding: 16px 20px;
}

.feedback-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
}

.status-banner--success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.status-banner--danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.status-banner--warning {
  background: #fffbeb;
  border: 1px solid #fef3c7;
}

.status-icon {
  font-size: 32px;
  line-height: 1;
  flex-shrink: 0;
}

.status-icon--success {
  color: #16a34a;
}

.status-icon--danger {
  color: #dc2626;
}

.status-icon--warning {
  color: #d97706;
}

.status-info {
  flex: 1;
}

.status-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 4px;
}

.status-banner--success .status-title {
  color: #15803d;
}

.status-banner--danger .status-title {
  color: #b91c1c;
}

.status-banner--warning .status-title {
  color: #b45309;
}

.status-subtitle {
  font-size: 13px;
  margin: 0;
  color: #4b5563;
}

.details-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
}

.stat-box {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px 14px;
}

.stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.stat-value.text-success {
  color: #16a34a;
}

.error-list-container {
  background: #ffffff;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px 16px;
}

.error-list-title {
  font-size: 13px;
  font-weight: 600;
  color: #b91c1c;
  margin-bottom: 8px;
}

.error-list {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.error-item code {
  color: #991b1b;
  background: #fee2e2;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  word-break: break-word;
}

.issues-table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  max-height: 280px;
  overflow-y: auto;
}

.issues-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.issues-table th {
  background: #f9fafb;
  padding: 8px 12px;
  font-weight: 600;
  color: #374151;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.issues-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.issues-table tr:last-child td {
  border-bottom: none;
}

.type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: #e5e7eb;
  color: #374151;
}

.type-badge--product {
  background: #e0f2fe;
  color: #0369a1;
}

.type-badge--warehouse {
  background: #fef3c7;
  color: #b45309;
}

.type-badge--measure_unit {
  background: #f3e8ff;
  color: #7e22ce;
}

.type-badge--subunitate {
  background: #dbeafe;
  color: #1d4ed8;
}

.type-badge--parcel {
  background: #dcfce7;
  color: #15803d;
}

.feedback-hint {
  font-size: 12px;
  color: #6b7280;
  padding: 6px 10px;
  border-radius: 4px;
  background: #f9fafb;
}

.feedback-hint--danger {
  color: #b91c1c;
  background: #fef2f2;
}

.feedback-hint--warning {
  color: #92400e;
  background: #fffbeb;
}

.feedback-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.field-badge {
  color: #1e40af;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.item-sub-key {
  margin-top: 2px;
}

.item-sub-key code {
  color: #6b7280;
  background: #f3f4f6;
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 3px;
}

.configure-btn {
  font-size: 11px;
  padding: 3px 8px;
  white-space: nowrap;
}
</style>
